import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { attorneyRatingAPI, ratingAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const LANGUAGE_OPTIONS = [
  { key: 'english', label: 'Инглиз' },
  { key: 'russian', label: 'Рус' },
  { key: 'french', label: 'Француз' },
  { key: 'german', label: 'Немис' },
  { key: 'spanish', label: 'Испан' },
  { key: 'arabic', label: 'Араб' },
  { key: 'chinese', label: 'Хитой' },
  { key: 'korean', label: 'Корейс' },
  { key: 'japanese', label: 'Япон' },
  { key: 'turkish', label: 'Турк' },
  { key: 'other', label: 'Бошқа' },
];
const LANGUAGE_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
const LEVEL_POINTS = { A1: 0.5, A2: 0.5, B1: 0.5, B2: 1, C1: 1, C2: 1 };

const PRACTICE_MAX = 15;
const CERTIFICATE_MAX = 3;
const LANGUAGE_MAX = 2;

// 1 point per full year of practice, plus the current year's months prorated out of 10
// (e.g. 3 months = 0.3 points); 10 and 11 months both give the full extra point. Capped.
const practicePoints = (years, months) => {
  const monthsCapped = Math.min(Number(months) || 0, 10);
  const points = (Number(years) || 0) + monthsCapped / 10;
  return Math.min(Math.round(points * 10) / 10, PRACTICE_MAX);
};

// Having at least one qualification certificate is worth the full CERTIFICATE_MAX — this is a
// pass/fail criterion, not counted per document.
const certificatePoints = (count) => (count > 0 ? CERTIFICATE_MAX : 0);

// B2/C1/C2 = 1 point, A1/A2/B1 = 0.5 point per language; the language block caps at LANGUAGE_MAX.
const languageRowPoints = (level) => LEVEL_POINTS[level] || 0;
const languageTotalPoints = (entries) => Math.min(entries.reduce((sum, l) => sum + languageRowPoints(l.level), 0), LANGUAGE_MAX);

const INVESTIGATION_DOC_POINTS = 0.5;
const INVESTIGATION_MAX = 4;
const COURT_CATEGORY_MAX = 4;
const CONTRACT_MAX = 5;
const COURT_CATEGORIES = [
  { key: 'criminal', field: 'court_criminal_orders', label: 'Жиноят' },
  { key: 'civil', field: 'court_civil_orders', label: 'Фуқаролик' },
  { key: 'administrative', field: 'court_administrative_orders', label: 'Маъмурий' },
  { key: 'economic', field: 'court_economic_orders', label: 'Иқтисодий' },
];

// 0.5 point per uploaded ордер, capped at INVESTIGATION_MAX (max 8 documents).
const investigationPoints = (count) => Math.min((count || 0) * INVESTIGATION_DOC_POINTS, INVESTIGATION_MAX);
// 0.5 point per ордер, each category capped at COURT_CATEGORY_MAX (4 × 4 = 16 block max).
const courtCategoryPoints = (count) => Math.min(count * 0.5, COURT_CATEGORY_MAX);
const courtTotalPoints = (form) => COURT_CATEGORIES.reduce((sum, c) => sum + courtCategoryPoints((form[c.field] || []).length), 0);
// 1 point per contract, capped at CONTRACT_MAX.
const contractPoints = (count) => Math.min(count, CONTRACT_MAX);

// The point a single row (0-based index) adds once the list is capped — 0 once earlier rows
// already hit the cap, so documents uploaded past the cap correctly show +0.
const rowPoints = (index, unit, max) => Math.min((index + 1) * unit, max) - Math.min(index * unit, max);

const SATISFACTION_MAX = 20;
// Each client review is worth stars/5 point (a 5★ review = 1 point); summed across all
// reviews and capped. average*count recovers the raw star sum without re-fetching every review.
const satisfactionPoints = (average, count) => Math.min(Math.round(((average * count) / 5) * 10) / 10, SATISFACTION_MAX);

const LICENSE_SCOPE_POINTS = 1.5;
const OFFICE_INFO_ITEM_POINTS = 0.5;
const OFFICE_HOURS_POINTS = 1;
const ARTICLE_MAX = 2;
const ARTICLES_PER_POINT = 5;
const SOCIAL_ITEM_POINTS = 0.5;

const PHONE_FORMAT = /^\+998 \d{2} \d{3} \d{2} \d{2}$/;
const TELEGRAM_FORMAT = /^https:\/\/t\.me\/.+/;

const licenseScopePoints = (fileUrl) => (fileUrl ? LICENSE_SCOPE_POINTS : 0);
const officeInfoPoints = (form) => [
  !!form.office_address,
  !!form.office_location_link,
  (form.office_photos || []).length > 0,
  PHONE_FORMAT.test(form.office_phone || ''),
].reduce((sum, filled) => sum + (filled ? OFFICE_INFO_ITEM_POINTS : 0), 0);
const officeHoursPoints = (form) => (form.office_hours_days && form.office_hours_start && form.office_hours_end ? OFFICE_HOURS_POINTS : 0);
// 1 point per ARTICLES_PER_POINT published articles, proportional, capped.
const articlePoints = (count) => Math.min(Math.round((count / ARTICLES_PER_POINT) * 10) / 10, ARTICLE_MAX);
const socialPoints = (form) => [
  !!form.social_website,
  TELEGRAM_FORMAT.test(form.social_telegram || ''),
  !!form.social_instagram,
  !!form.social_youtube,
].reduce((sum, filled) => sum + (filled ? SOCIAL_ITEM_POINTS : 0), 0);

const OFFICE_HOURS_DAYS_OPTIONS = [
  { value: 'mon_fri', label: 'Душанба – Жума' },
  { value: 'mon_sat', label: 'Душанба – Шанба' },
  { value: 'every_day', label: 'Ҳар куни' },
];
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, h) => ({ value: String(h).padStart(2, '0'), label: `${String(h).padStart(2, '0')}:00` }));

const emptyForm = () => ({
  status: 'draft',
  training_courses: [],
  years_of_practice_years: 0,
  years_of_practice_months: 0,
  years_of_practice_file_url: '',
  foreign_language_entries: [],
  investigation_participation_files: [],
  court_criminal_orders: [],
  court_civil_orders: [],
  court_administrative_orders: [],
  court_economic_orders: [],
  legal_entity_contracts: [],
  no_disciplinary_sanctions: false,
  no_disciplinary_sanctions_file: '',
  ethics_compliance: false,
  peer_references: [],
  recommendation_letters: [],
  license_econ_civil_file: '',
  license_admin_criminal_file: '',
  office_address: '',
  office_location_link: '',
  office_photos: [],
  office_phone: '',
  office_hours_days: '',
  office_hours_start: '',
  office_hours_end: '',
  social_website: '',
  social_telegram: '',
  social_instagram: '',
  social_youtube: '',
  scientific_activity: [],
});

const RatingSubmission = () => {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [form, setForm] = useState(emptyForm());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null); // field name currently uploading, or null
  const [error, setError] = useState('');
  const [savedToast, setSavedToast] = useState(false);
  // Мижозлар қониқиши is read-only — it's computed from this attorney's existing client star
  // ratings, not something they fill in.
  const [satisfaction, setSatisfaction] = useState({ average: 0, count: 0 });
  // Мақола is read-only too — computed server-side from this attorney's published articles.
  const [articlesCount, setArticlesCount] = useState(0);

  useEffect(() => {
    attorneyRatingAPI.getMine()
      .then(res => {
        setForm({
          ...emptyForm(),
          ...res.data,
          foreign_language_entries: res.data.foreign_language_entries || [],
          court_criminal_orders: res.data.court_criminal_orders || [],
          court_civil_orders: res.data.court_civil_orders || [],
          court_administrative_orders: res.data.court_administrative_orders || [],
          court_economic_orders: res.data.court_economic_orders || [],
          legal_entity_contracts: res.data.legal_entity_contracts || [],
          office_photos: res.data.office_photos || [],
        });
        setArticlesCount(res.data.published_articles_count || 0);
      })
      .catch(err => setError(err.response?.data?.error || 'Юклашда хатолик'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!profile?.id) return;
    ratingAPI.get(profile.id)
      .then(res => setSatisfaction({ average: res.data.average || 0, count: res.data.count || 0 }))
      .catch(() => {});
  }, [profile?.id]);

  const locked = form.status === 'reviewed';

  const setField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const addListItem = (field, shape) => setForm(prev => ({ ...prev, [field]: [...prev[field], shape] }));
  const removeListItem = (field, idx) => setForm(prev => ({ ...prev, [field]: prev[field].filter((_, i) => i !== idx) }));
  const updateListItem = (field, idx, key, value) => setForm(prev => ({
    ...prev,
    [field]: prev[field].map((item, i) => (i === idx ? { ...item, [key]: value } : item)),
  }));

  const uploadFor = async (field, category, file) => {
    setUploading(field);
    try {
      const res = await attorneyRatingAPI.uploadFile(file, category);
      setField(field, res.data.url);
    } catch (err) {
      setError(err.response?.data?.error || 'Юклашда хатолик');
    } finally {
      setUploading(null);
    }
  };

  const uploadForList = async (field, category, idx, file) => {
    setUploading(`${field}-${idx}`);
    try {
      const res = await attorneyRatingAPI.uploadFile(file, category);
      updateListItem(field, idx, 'fileUrl', res.data.url);
    } catch (err) {
      setError(err.response?.data?.error || 'Юклашда хатолик');
    } finally {
      setUploading(null);
    }
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await attorneyRatingAPI.updateMine(form);
      setForm(prev => ({ ...prev, ...res.data }));
      setSavedToast(true);
      setTimeout(() => setSavedToast(false), 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Сақлашда хатолик');
    } finally {
      setSaving(false);
    }
  };

  // Only blocks on a partially-filled Касбий малака row (stated years/months with no doc, or a
  // language row with no doc) — leaving the whole block empty must never block other sections.
  const validateQualification = () => {
    if (practicePoints(form.years_of_practice_years, form.years_of_practice_months) > 0 && !form.years_of_practice_file_url) {
      return 'Адвокатлик стажини тасдиқловчи ҳужжатни юкланг';
    }
    if (form.foreign_language_entries.some(l => !l.fileUrl)) {
      return 'Ҳар бир чет тили учун тасдиқловчи ҳужжат юкланг';
    }
    return '';
  };

  const submit = async () => {
    const validationError = validateQualification();
    if (validationError) { setError(validationError); return; }
    await save();
    setSaving(true);
    try {
      const res = await attorneyRatingAPI.submitMine();
      setForm(prev => ({ ...prev, ...res.data }));
      navigate('/attorney/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Юборишда хатолик');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={s.center}>Юкланмоқда...</div>;

  return (
    <div style={s.page}>
      <div style={s.container}>
        <h1 style={s.title}>AdvokatTop дан шахсий рейтинг олиш</h1>
        <p style={s.subtitle}>
          Қуйидаги маълумотларни тўлдиринг — улар асосида AdvokatTop сизнинг касбий рейтингингизни шакллантиради.
          {' '}Рейтинг қандай ҳисобланиши ҳақида батафсил: <Link to="/rating-methodology" style={{ color: '#3182ce', fontWeight: '600' }}>рейтинг тизими методологияси</Link>.
        </p>

        {locked && (
          <div style={s.lockedBanner}>Аризангиз кўриб чиқилмоқда — тахрирлаш вақтинча ёпилган.</div>
        )}
        {error && <div style={s.error}>{error}</div>}
        {savedToast && <div style={s.toast}>Сақланди!</div>}

        <fieldset disabled={locked || saving} style={s.fieldset}>
          {/* Q — Касбий малака */}
          <Section title="Касбий малака">
            <Label>Адвокатлик стажи</Label>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <input style={{ ...s.input, maxWidth: '110px' }} type="number" min="0" placeholder="Йил"
                value={form.years_of_practice_years}
                onChange={e => setField('years_of_practice_years', e.target.value === '' ? 0 : Number(e.target.value))} />
              <span style={{ color: '#718096', fontSize: '0.9rem' }}>йил</span>
              <StyledSelect style={{ maxWidth: '110px' }} value={form.years_of_practice_months}
                onChange={v => setField('years_of_practice_months', Number(v))}
                options={Array.from({ length: 12 }, (_, m) => ({ value: m, label: String(m) }))} />
              <span style={{ color: '#718096', fontSize: '0.9rem' }}>ой</span>
              <PointBadge style={{ marginLeft: 'auto' }} value={practicePoints(form.years_of_practice_years, form.years_of_practice_months)} />
            </div>
            <div style={s.hint}>Стажни тасдиқлаш учун my.mehnat.uz дан маълумотномани юкланг</div>
            <FileButton uploading={uploading === 'years_of_practice_file_url'} hasFile={!!form.years_of_practice_file_url}
              onFile={f => uploadFor('years_of_practice_file_url', 'years_of_practice', f)} />

            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginTop: '1.25rem' }}>
              <Label>Адвокатнинг касбий малака оширганлиги тўғрисида сертификат</Label>
              <PointBadge value={certificatePoints(form.training_courses.length)} />
            </div>
            <div style={s.hint}>Охирги 3 йилда олинган сертификат инобатга олинади</div>
            {form.training_courses.map((c, i) => (
              <ListRow key={i} onRemove={() => removeListItem('training_courses', i)}>
                <span style={s.fileName}>{c.originalName || c.url || 'Файл'}</span>
              </ListRow>
            ))}
            <FileButton uploading={uploading === 'training_courses'} hasFile={false}
              onFile={async (file) => {
                setUploading('training_courses');
                try {
                  const res = await attorneyRatingAPI.uploadFile(file, 'training_course');
                  addListItem('training_courses', { url: res.data.url, originalName: res.data.originalName });
                } catch (err) { setError(err.response?.data?.error || 'Юклашда хатолик'); }
                finally { setUploading(null); }
              }} label="Ҳужжат қўшиш" />

            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginTop: '1.25rem' }}>
              <Label>Чет тиллари</Label>
              <PointBadge value={languageTotalPoints(form.foreign_language_entries)} />
            </div>
            {form.foreign_language_entries.map((l, i) => (
              <ListRow key={i} onRemove={() => removeListItem('foreign_language_entries', i)}>
                <StyledSelect style={{ minWidth: '140px' }} value={l.language || ''} placeholder="Тил"
                  onChange={v => updateListItem('foreign_language_entries', i, 'language', v)}
                  options={LANGUAGE_OPTIONS.map(opt => ({ value: opt.key, label: opt.label }))} />
                {l.language === 'other' && (
                  <input style={s.input} placeholder="Қайси тил?" value={l.customLanguage || ''}
                    onChange={e => updateListItem('foreign_language_entries', i, 'customLanguage', e.target.value)} />
                )}
                <StyledSelect style={{ minWidth: '110px' }} value={l.level || ''} placeholder="Даража"
                  onChange={v => updateListItem('foreign_language_entries', i, 'level', v)}
                  options={LANGUAGE_LEVELS.map(lv => ({ value: lv, label: lv }))} />
                <FileButton uploading={uploading === `foreign_language_entries-${i}`} hasFile={!!l.fileUrl}
                  onFile={f => uploadForList('foreign_language_entries', 'foreign_language', i, f)} label="Ҳужжат (мажбурий)"
                  style={{ marginTop: 0, padding: '0.6rem 0.8rem', fontSize: '0.9rem', border: '1px solid #e2e8f0', display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' }} />
                <PointBadge value={languageRowPoints(l.level)} />
              </ListRow>
            ))}
            <AddButton onClick={() => addListItem('foreign_language_entries', { language: '', customLanguage: '', level: '', fileUrl: '' })}>+ Тил қўшиш</AddButton>
          </Section>

          {/* E — Амалий тажриба */}
          <Section title="Амалий тажриба">
            <div style={s.criterionBlock}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <div style={s.criterionTitle}>Терговда иштирок этганлик</div>
                <PointBadge value={investigationPoints((form.investigation_participation_files || []).length)} />
              </div>
              {(form.investigation_participation_files || []).map((f, i) => (
                <ListRow key={i} onRemove={() => removeListItem('investigation_participation_files', i)}>
                  <span style={s.fileName}>{f.originalName || f.url || 'Файл'}</span>
                  <PointBadge value={rowPoints(i, INVESTIGATION_DOC_POINTS, INVESTIGATION_MAX)} />
                </ListRow>
              ))}
              <FileButton uploading={uploading === 'investigation_participation_files'} hasFile={false}
                onFile={async (file) => {
                  setUploading('investigation_participation_files');
                  try {
                    const res = await attorneyRatingAPI.uploadFile(file, 'investigation_order');
                    addListItem('investigation_participation_files', { url: res.data.url, originalName: res.data.originalName });
                  } catch (err) {
                    setError(err.response?.data?.error || 'Юклашда хатолик');
                  } finally { setUploading(null); }
                }} label="Ордер қўшиш" />
            </div>

            <div style={s.criterionBlock}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                <div style={s.criterionTitle}>Судларда иштирок этганлик</div>
                <PointBadge value={courtTotalPoints(form)} />
              </div>
              {COURT_CATEGORIES.map(cat => (
                <div key={cat.key} style={s.subBlock}>
                  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                    <div style={s.subTitle}>{cat.label}</div>
                    <PointBadge value={courtCategoryPoints((form[cat.field] || []).length)} />
                  </div>
                  {(form[cat.field] || []).map((f, i) => (
                    <ListRow key={i} onRemove={() => removeListItem(cat.field, i)}>
                      <span style={s.fileName}>{f.originalName || f.url || 'Файл'}</span>
                      <PointBadge value={rowPoints(i, 0.5, COURT_CATEGORY_MAX)} />
                    </ListRow>
                  ))}
                  <FileButton uploading={uploading === cat.field} hasFile={false}
                    onFile={async (file) => {
                      setUploading(cat.field);
                      try {
                        const res = await attorneyRatingAPI.uploadFile(file, `court_${cat.key}`);
                        addListItem(cat.field, { url: res.data.url, originalName: res.data.originalName });
                      } catch (err) {
                        setError(err.response?.data?.error || 'Юклашда хатолик');
                      } finally { setUploading(null); }
                    }} label="Ордер қўшиш" />
                </div>
              ))}
            </div>

            <div>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <div style={s.criterionTitle}>Юридик шахсларга обуна асосида юридик хизмат кўрсатиш</div>
                <PointBadge value={contractPoints(form.legal_entity_contracts.length)} />
              </div>
              {form.legal_entity_contracts.map((f, i) => (
                <ListRow key={i} onRemove={() => removeListItem('legal_entity_contracts', i)}>
                  <span style={s.fileName}>{f.originalName || f.url || 'Файл'}</span>
                  <PointBadge value={rowPoints(i, 1, CONTRACT_MAX)} />
                </ListRow>
              ))}
              <FileButton uploading={uploading === 'legal_entity_contracts'} hasFile={false}
                onFile={async (file) => {
                  setUploading('legal_entity_contracts');
                  try {
                    const res = await attorneyRatingAPI.uploadFile(file, 'legal_entity_contract');
                    addListItem('legal_entity_contracts', { url: res.data.url, originalName: res.data.originalName });
                  } catch (err) {
                    setError(err.response?.data?.error || 'Юклашда хатолик');
                  } finally { setUploading(null); }
                }} label="Шартнома қўшиш" />
            </div>
          </Section>

          {/* C — Мижозлар қониқиши (read-only — computed from this attorney's client star ratings) */}
          <Section title="Мижозлар қониқиши">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <StarDisplay average={satisfaction.average} />
                <span style={{ fontSize: '0.9rem', color: '#4a5568' }}>
                  {satisfaction.average} ({satisfaction.count} та шарҳ)
                </span>
              </div>
              <PointBadge value={satisfactionPoints(satisfaction.average, satisfaction.count)} />
            </div>
            <div style={s.hint}>
              Ҳар бир мижоз шарҳи юлдузига мутаносиб балл беради (5 юлдуз = 1 балл) — автоматик ҳисобланади, қўшимча ҳужжат керак эмас.
            </div>
          </Section>

          {/* R — Касбий обрў */}
          <Section title="Касбий обрў">
            <label style={s.checkboxLabel}>
              <input type="checkbox" checked={form.no_disciplinary_sanctions} onChange={e => setField('no_disciplinary_sanctions', e.target.checked)} />
              Интизомий жазоларим мавжуд эмаслигини тасдиқлайман
            </label>
            <FileButton uploading={uploading === 'no_disciplinary_sanctions_file'} hasFile={!!form.no_disciplinary_sanctions_file}
              onFile={f => uploadFor('no_disciplinary_sanctions_file', 'no_sanctions', f)} />

            <label style={{ ...s.checkboxLabel, marginTop: '1rem' }}>
              <input type="checkbox" checked={form.ethics_compliance} onChange={e => setField('ethics_compliance', e.target.checked)} />
              Касбий этика қоидаларига риоя қилишимни тасдиқлайман
            </label>

            <Label style={{ marginTop: '1.25rem' }}>Ҳамкасблар тавсиялари</Label>
            {form.peer_references.map((f, i) => (
              <ListRow key={i} onRemove={() => removeListItem('peer_references', i)}>
                <span style={s.fileName}>{f.originalName || 'Файл'}</span>
              </ListRow>
            ))}
            <FileButton uploading={uploading === 'peer_references'} hasFile={false}
              onFile={async (file) => {
                setUploading('peer_references');
                try {
                  const res = await attorneyRatingAPI.uploadFile(file, 'peer_reference');
                  addListItem('peer_references', { url: res.data.url, originalName: res.data.originalName });
                } catch (err) { setError(err.response?.data?.error || 'Юклашда хатолик'); }
                finally { setUploading(null); }
              }} label="Файл қўшиш" />

            <Label style={{ marginTop: '1.25rem' }}>Тавсиянома хатлар</Label>
            {form.recommendation_letters.map((f, i) => (
              <ListRow key={i} onRemove={() => removeListItem('recommendation_letters', i)}>
                <span style={s.fileName}>{f.originalName || 'Файл'}</span>
              </ListRow>
            ))}
            <FileButton uploading={uploading === 'recommendation_letters'} hasFile={false}
              onFile={async (file) => {
                setUploading('recommendation_letters');
                try {
                  const res = await attorneyRatingAPI.uploadFile(file, 'recommendation_letter');
                  addListItem('recommendation_letters', { url: res.data.url, originalName: res.data.originalName });
                } catch (err) { setError(err.response?.data?.error || 'Юклашда хатолик'); }
                finally { setUploading(null); }
              }} label="Файл қўшиш" />
          </Section>

          {/* D — Очиқлик ва рақамли фаоллик */}
          <Section title="Очиқлик ва рақамли фаоллик">
            <div style={s.criterionBlock}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                <div style={s.criterionTitle}>Лицензия</div>
                <PointBadge value={licenseScopePoints(form.license_econ_civil_file) + licenseScopePoints(form.license_admin_criminal_file)} />
              </div>
              <div style={s.subBlock}>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                  <div style={s.subTitle}>Иқтисодий ва фуқаролик ишлар бўйича</div>
                  <PointBadge value={licenseScopePoints(form.license_econ_civil_file)} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <FileButton uploading={uploading === 'license_econ_civil_file'} hasFile={!!form.license_econ_civil_file}
                    onFile={f => uploadFor('license_econ_civil_file', 'license_econ_civil', f)} />
                  {form.license_econ_civil_file && (
                    <button type="button" onClick={() => setField('license_econ_civil_file', '')} style={s.removeBtn}>✕</button>
                  )}
                </div>
              </div>
              <div style={s.subBlock}>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                  <div style={s.subTitle}>Маъмурий ва жиноий ишлар бўйича</div>
                  <PointBadge value={licenseScopePoints(form.license_admin_criminal_file)} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <FileButton uploading={uploading === 'license_admin_criminal_file'} hasFile={!!form.license_admin_criminal_file}
                    onFile={f => uploadFor('license_admin_criminal_file', 'license_admin_criminal', f)} />
                  {form.license_admin_criminal_file && (
                    <button type="button" onClick={() => setField('license_admin_criminal_file', '')} style={s.removeBtn}>✕</button>
                  )}
                </div>
              </div>
            </div>

            <div style={s.criterionBlock}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                <div style={s.criterionTitle}>Офис маълумотлари</div>
                <PointBadge value={officeInfoPoints(form)} />
              </div>

              <Label>Манзил (аниқ манзилни ёзинг)</Label>
              <input style={s.input} placeholder="Масалан: Тошкент ш., Чилонзор тумани, ..." value={form.office_address}
                onChange={e => setField('office_address', e.target.value)} />

              <Label style={{ marginTop: '0.9rem' }}>Локация (харитада белгиланг)</Label>
              <div style={s.hint}>
                Google Maps-да офисингизни топинг, "Улашиш" (Share) тугмасидан ҳаволани нусхалаб, қуйига қўйинг
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <input style={s.input} placeholder="https://maps.app.goo.gl/..." value={form.office_location_link}
                  onChange={e => setField('office_location_link', e.target.value)} />
                <a href="https://www.google.com/maps" target="_blank" rel="noopener noreferrer" style={{ ...s.fileBtn, marginTop: 0, textDecoration: 'none', whiteSpace: 'nowrap' }}>
                  Google Maps-ни очиш
                </a>
              </div>

              <Label style={{ marginTop: '0.9rem' }}>Офис расмлари</Label>
              {(form.office_photos || []).map((f, i) => (
                <ListRow key={i} onRemove={() => removeListItem('office_photos', i)}>
                  <span style={s.fileName}>{f.originalName || f.url || 'Файл'}</span>
                </ListRow>
              ))}
              <FileButton uploading={uploading === 'office_photos'} hasFile={false}
                onFile={async (file) => {
                  setUploading('office_photos');
                  try {
                    const res = await attorneyRatingAPI.uploadFile(file, 'office_photo');
                    addListItem('office_photos', { url: res.data.url, originalName: res.data.originalName });
                  } catch (err) { setError(err.response?.data?.error || 'Юклашда хатолик'); }
                  finally { setUploading(null); }
                }} label="Расм қўшиш" />

              <Label style={{ marginTop: '0.9rem' }}>Телефон</Label>
              <input style={s.input} placeholder="+998 00 000 00 00" value={form.office_phone}
                onChange={e => setField('office_phone', e.target.value)} />
            </div>

            <div style={s.criterionBlock}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                <div style={s.criterionTitle}>Иш вақти</div>
                <PointBadge value={officeHoursPoints(form)} />
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <StyledSelect style={{ minWidth: '180px' }} value={form.office_hours_days} placeholder="Кунлар"
                  onChange={v => setField('office_hours_days', v)} options={OFFICE_HOURS_DAYS_OPTIONS} />
                <StyledSelect style={{ minWidth: '110px' }} value={form.office_hours_start} placeholder="Бошланиши"
                  onChange={v => setField('office_hours_start', v)} options={HOUR_OPTIONS} />
                <StyledSelect style={{ minWidth: '110px' }} value={form.office_hours_end} placeholder="Тугаши"
                  onChange={v => setField('office_hours_end', v)} options={HOUR_OPTIONS} />
              </div>
            </div>

            <div style={s.criterionBlock}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                <div style={s.criterionTitle}>Мақола</div>
                <PointBadge value={articlePoints(articlesCount)} />
              </div>
              <div style={s.hint}>
                AdvokatTop сайтида чоп этилган мақолаларингиз сони автоматик ҳисобланади (ҳар 5 мақола учун 1 балл) — {articlesCount} та мақола
              </div>
            </div>

            <div>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                <div style={s.criterionTitle}>Ижтимоий тармоқларда фаоллик</div>
                <PointBadge value={socialPoints(form)} />
              </div>
              <Label>Сайт</Label>
              <input style={s.input} placeholder="https://..." value={form.social_website}
                onChange={e => setField('social_website', e.target.value)} />
              <Label style={{ marginTop: '0.9rem' }}>Телеграм</Label>
              <input style={s.input} placeholder="https://t.me/..." value={form.social_telegram}
                onChange={e => setField('social_telegram', e.target.value)} />
              <Label style={{ marginTop: '0.9rem' }}>Инстаграм</Label>
              <input style={s.input} placeholder="https://instagram.com/..." value={form.social_instagram}
                onChange={e => setField('social_instagram', e.target.value)} />
              <Label style={{ marginTop: '0.9rem' }}>Ютуб</Label>
              <input style={s.input} placeholder="https://youtube.com/..." value={form.social_youtube}
                onChange={e => setField('social_youtube', e.target.value)} />
            </div>
          </Section>

          {/* S — Илмий ва ижтимоий фаолият */}
          <Section title="Илмий ва ижтимоий фаолият">
            <Label>Китоблар, илмий мақолалар, конференциялар, ҳуқуқий тарғибот ишлари</Label>
            {form.scientific_activity.map((a, i) => (
              <ListRow key={i} onRemove={() => removeListItem('scientific_activity', i)}>
                <select style={s.select} value={a.type || ''} onChange={e => updateListItem('scientific_activity', i, 'type', e.target.value)}>
                  <option value="">Тури</option>
                  <option value="book">Китоб</option>
                  <option value="article">Илмий мақола</option>
                  <option value="conference">Конференция</option>
                  <option value="advocacy">Ҳуқуқий тарғибот</option>
                </select>
                <input style={s.input} placeholder="Сарлавҳа" value={a.title || ''} onChange={e => updateListItem('scientific_activity', i, 'title', e.target.value)} />
                <input style={s.input} placeholder="Ҳавола (ихтиёрий)" value={a.link || ''} onChange={e => updateListItem('scientific_activity', i, 'link', e.target.value)} />
              </ListRow>
            ))}
            <AddButton onClick={() => addListItem('scientific_activity', { type: '', title: '', link: '', fileUrl: '' })}>+ Қўшиш</AddButton>
          </Section>
        </fieldset>

        {!locked && (
          <div style={s.actions}>
            <button onClick={save} disabled={saving} style={s.btnSecondary}>
              {saving ? 'Сақланмоқда...' : 'Қораламани сақлаш'}
            </button>
            <button onClick={submit} disabled={saving} style={s.btnPrimary}>
              {saving ? 'Юборилмоқда...' : 'Кўриб чиқишга юбориш'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div style={s.section}>
    <h2 style={s.sectionTitle}>{title}</h2>
    {children}
  </div>
);

const Label = ({ children, style }) => <div style={{ ...s.label, ...style }}>{children}</div>;

const PointBadge = ({ value, style }) => (
  <span style={{ fontSize: '0.85rem', color: '#4a5568', fontWeight: '600', ...style }}>
    Балл: <span style={{ color: '#276749', fontWeight: '800' }}>+{value}</span>
  </span>
);

// Read-only 5-star display, filled up to the rounded average (matches the ★/#f6ad55 convention
// used by AttorneyProfile.jsx's StarRatingPopup).
const StarDisplay = ({ average }) => {
  const filled = Math.round(average);
  return (
    <span style={{ fontSize: '1.3rem', lineHeight: 1 }}>
      {[1, 2, 3, 4, 5].map(i => (
        <span key={i} style={{ color: i <= filled ? '#f6ad55' : '#e2e8f0' }}>★</span>
      ))}
    </span>
  );
};

const ListRow = ({ children, onRemove }) => (
  <div style={s.listRow}>
    {children}
    <button type="button" onClick={onRemove} style={s.removeBtn}>✕</button>
  </div>
);

const AddButton = ({ children, onClick }) => (
  <button type="button" onClick={onClick} style={s.addBtn}>{children}</button>
);

// Custom-styled dropdown (button trigger + absolute panel) matching the pattern used across
// the site (FindAttorney.jsx's FilterDropdown, AttorneyRequests.jsx's LocationDropdown, etc.)
// instead of a native <select>.
const StyledSelect = ({ value, onChange, options, placeholder = 'Танланг', style }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const selected = options.find(o => String(o.value) === String(value));
  return (
    <div ref={ref} style={{ position: 'relative', ...style }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '0.6rem 0.8rem', border: '1px solid #e2e8f0', borderRadius: '8px',
          backgroundColor: 'white', fontSize: '0.9rem', cursor: 'pointer',
          color: selected ? '#2d3748' : '#a0aec0',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selected ? selected.label : placeholder}</span>
        <svg width="10" height="6" viewBox="0 0 10 6" style={{ opacity: 0.5, flexShrink: 0, marginLeft: '0.4rem', transition: 'transform 0.15s', transform: open ? 'rotate(180deg)' : 'none' }}>
          <path d="M1 1l4 4 4-4" stroke="#718096" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 50,
          backgroundColor: 'white', borderRadius: '10px', border: '1px solid #e2e8f0',
          boxShadow: '0 8px 32px rgba(0,0,0,0.14)', maxHeight: '220px', overflowY: 'auto', padding: '0.4rem 0',
        }}>
          {options.map(opt => (
            <StyledOption key={opt.value} active={String(value) === String(opt.value)} onClick={() => { onChange(opt.value); setOpen(false); }}>
              {opt.label}
            </StyledOption>
          ))}
        </div>
      )}
    </div>
  );
};

const StyledOption = ({ children, active, onClick }) => {
  const [hover, setHover] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        padding: '0.55rem 0.9rem', fontSize: '0.88rem', cursor: 'pointer',
        color: active ? '#2b6cb0' : '#2d3748', fontWeight: active ? '600' : '400',
        backgroundColor: hover ? '#f7fafc' : 'transparent', whiteSpace: 'nowrap',
      }}
    >
      {children}
    </div>
  );
};

const FileButton = ({ onFile, uploading, hasFile, label, style }) => (
  <label style={{ ...s.fileBtn, ...style }}>
    {uploading ? 'Юкланмоқда...' : hasFile ? '✓ Юкланди — алмаштириш' : (label || 'Файл юклаш')}
    <input
      type="file"
      accept="image/jpeg,image/png,image/webp,application/pdf"
      style={{ display: 'none' }}
      onChange={e => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ''; }}
    />
  </label>
);

const s = {
  page: { backgroundColor: '#f7fafc', minHeight: 'calc(100vh - 120px)', padding: '3rem 1.5rem' },
  container: { maxWidth: '720px', margin: '0 auto' },
  center: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh', color: '#718096' },
  title: { fontSize: '1.6rem', fontWeight: '800', color: '#1a365d', marginBottom: '0.4rem' },
  subtitle: { color: '#718096', fontSize: '0.95rem', marginBottom: '1.5rem', lineHeight: 1.6 },
  lockedBanner: { backgroundColor: '#fffbeb', border: '1px solid #f59e0b', color: '#92400e', borderRadius: '10px', padding: '0.9rem 1.1rem', marginBottom: '1.25rem', fontSize: '0.9rem' },
  error: { backgroundColor: '#fed7d7', color: '#c53030', padding: '0.75rem 1rem', borderRadius: '8px', fontSize: '0.9rem', marginBottom: '1rem' },
  toast: { backgroundColor: '#c6f6d5', color: '#276749', padding: '0.6rem 1rem', borderRadius: '8px', fontSize: '0.9rem', marginBottom: '1rem' },
  fieldset: { border: 'none', padding: 0, margin: 0 },
  section: { backgroundColor: 'white', borderRadius: '14px', padding: '1.5rem', boxShadow: '0 2px 10px rgba(0,0,0,0.06)', marginBottom: '1.25rem' },
  sectionTitle: { fontSize: '1.1rem', fontWeight: '700', color: '#1a365d', marginBottom: '1rem' },
  label: { fontSize: '0.85rem', fontWeight: '600', color: '#4a5568', marginBottom: '0.4rem' },
  hint: { fontSize: '0.8rem', color: '#a0aec0', marginBottom: '0.5rem' },
  criterionBlock: { paddingBottom: '1.25rem', marginBottom: '1.25rem', borderBottom: '1px solid #edf2f7' },
  criterionTitle: { fontSize: '0.95rem', fontWeight: '700', color: '#1a365d' },
  subBlock: { backgroundColor: '#f7fafc', borderRadius: '10px', padding: '0.85rem 1rem', marginBottom: '0.75rem' },
  subTitle: { fontSize: '0.85rem', fontWeight: '600', color: '#4a5568', marginBottom: '0.5rem' },
  input: { flex: 1, padding: '0.6rem 0.8rem', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '0.9rem' },
  select: { padding: '0.6rem 0.8rem', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '0.9rem' },
  textarea: { width: '100%', minHeight: '80px', padding: '0.6rem 0.8rem', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '0.9rem', fontFamily: 'inherit', resize: 'vertical' },
  listRow: { display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.5rem' },
  removeBtn: { border: 'none', background: 'none', color: '#e53e3e', cursor: 'pointer', fontSize: '1rem', padding: '0.4rem' },
  addBtn: { border: '1px dashed #cbd5e0', background: 'none', borderRadius: '8px', padding: '0.5rem 1rem', color: '#4a5568', cursor: 'pointer', fontSize: '0.85rem' },
  fileBtn: { display: 'inline-block', border: '1px solid #cbd5e0', borderRadius: '8px', padding: '0.5rem 1rem', color: '#4a5568', cursor: 'pointer', fontSize: '0.85rem', marginTop: '0.5rem' },
  fileName: { flex: 1, fontSize: '0.9rem', color: '#4a5568' },
  checkboxLabel: { display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', color: '#4a5568', cursor: 'pointer' },
  actions: { display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1rem' },
  btnSecondary: { padding: '0.8rem 1.5rem', borderRadius: '10px', border: '1px solid #cbd5e0', backgroundColor: 'white', color: '#4a5568', fontWeight: '600', cursor: 'pointer' },
  btnPrimary: { padding: '0.8rem 1.5rem', borderRadius: '10px', border: 'none', backgroundColor: '#FFD700', color: '#0d1f17', fontWeight: '700', cursor: 'pointer' },
};

export default RatingSubmission;
